import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Table,
  Tag,
  Select,
  Empty,
  Button,
  Modal,
  Form,
  message,
  Descriptions,
  Drawer,
  DatePicker,
  Tabs,
  Dropdown,
} from "antd";
import {
  RotateCcw,
  Calendar,
  User as UserIcon,
  TrendingUp,
  TrendingDown,
  FileText,
  DollarSign,
  Undo2,
  Lock,
  Briefcase,
} from "lucide-react";
import { EyeOutlined, MoreOutlined, CheckCircleOutlined } from "@ant-design/icons";
import {
  useSuppliersQuery,
  useSupplierCreditsQuery,
  useSupplierCreditBalanceQuery,
  useSupplierOutstandingPurchasesQuery,
  useSupplierBulkPayMutation,
  useActivePaymentMethodsQuery,
  useActiveBankAccountsQuery,
  usePurchasesQuery,
  usePurchasePaymentsQuery,
} from "../../Services";
import { CustomerCreditTransactionType } from "../../Enums";
import { PaymentOption, PaymentOptionValue } from "../../components/PaymentsOption";
import { PaymentMethodDetails } from "../../components/PaymentMethodDetails/PaymentMethodDetails";
import dayjs from "dayjs";
import {
  PageHeader,
  drawerFooter,
  drawerTitle,
  modalTitle,
  tablePagination,
} from "../../components";
import { PAGE_SIZE } from "../../Constant";

export const SupplierLedgerPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlId = searchParams.get("id");

  const [supplierId, setSupplierId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("overview");

  // Pagination states
  const [creditPage, setCreditPage] = useState(1);
  const [purchasePage, setPurchasePage] = useState(1);
  const [returnPage, setReturnPage] = useState(1);
  const [paymentPage, setPaymentPage] = useState(1);

  // Modal and detail states
  const [isRepayOpen, setIsRepayOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);

  const [repayForm] = Form.useForm();

  const [paymentMethodCode, setPaymentMethodCode] = useState<string>("");
  const [paymentDetails, setPaymentDetails] = useState<PaymentOptionValue>({
    amount: 0,
    note: "",
    bank_account_id: "",
    transaction_no: "",
    reference_no: "",
    cheques: [],
  });
  const [bulkPayAmount, setBulkPayAmount] = useState<number>(0);

  // Queries
  const { data: suppliers = [] } = useSuppliersQuery();
  const { data: bankAccounts = [] } = useActiveBankAccountsQuery();
  const { data: paymentMethods = [] } = useActivePaymentMethodsQuery();

  // Set supplierId from URL or when URL parameter changes
  useEffect(() => {
    if (urlId) {
      setSupplierId(urlId);
    }
  }, [urlId]);

  const selectedSupplier = suppliers.find((s: any) => s.id === supplierId);

  const {
    data: supplierBalance,
    refetch: refetchBalance,
  } = useSupplierCreditBalanceQuery(supplierId || "", {
    enabled: !!supplierId,
  });

  const {
    data: creditLogs,
    isLoading: isCreditLoading,
    refetch: refetchCredits,
  } = useSupplierCreditsQuery(
    {
      page: creditPage,
      limit: PAGE_SIZE,
      supplier_id: supplierId || "",
    },
    { enabled: !!supplierId }
  );

  const {
    data: purchasesData,
    isLoading: isPurchasesLoading,
    refetch: refetchPurchases,
  } = usePurchasesQuery(
    {
      page: purchasePage,
      limit: PAGE_SIZE,
      supplier_id: supplierId || undefined,
    },
    { enabled: !!supplierId && activeTab === "purchases" }
  );

  const {
    data: returnsData,
    isLoading: isReturnsLoading,
    refetch: refetchReturns,
  } = usePurchasesQuery(
    {
      page: returnPage,
      limit: PAGE_SIZE,
      supplier_id: supplierId || undefined,
      status: "Returned",
    },
    { enabled: !!supplierId && activeTab === "returns" }
  );

  const {
    data: paymentsData,
    isLoading: isPaymentsLoading,
    refetch: refetchPayments,
  } = usePurchasePaymentsQuery(
    {
      page: paymentPage,
      limit: PAGE_SIZE,
      supplier_id: supplierId || undefined,
    },
    { enabled: !!supplierId && activeTab === "payments" }
  );

  const {
    data: outstandingPurchases = [],
    isLoading: isOutstandingLoading,
    refetch: refetchOutstanding,
  } = useSupplierOutstandingPurchasesQuery(supplierId || "", {
    enabled: !!supplierId && (activeTab === "outstanding" || isRepayOpen),
  });

  const bulkPayMutation = useSupplierBulkPayMutation();

  const handleRefreshAll = () => {
    if (supplierId) {
      refetchBalance();
      refetchCredits();
      refetchPurchases();
      refetchReturns();
      refetchPayments();
      refetchOutstanding();
    }
  };

  const handleSaveBulkPay = async (values: any) => {
    try {
      if (!supplierId) return;
      const pm = paymentMethods.find((m: any) => m.id === values.payment_method_id);
      if (!pm) {
        message.error("Invalid payment method.");
        return;
      }

      const res = await bulkPayMutation.mutateAsync({
        supplier_id: supplierId,
        amount: bulkPayAmount,
        payment_method_id: pm.id,
        bank_account_id: paymentDetails.bank_account_id || null,
        transaction_no: paymentDetails.transaction_no || null,
        reference_no: paymentDetails.reference_no || null,
        cheque_no: paymentDetails.cheque_no || null,
        bank_name: paymentDetails.bank_name || null,
        cheque_date: paymentDetails.cheque_date || null,
        transaction_date: values.transaction_date ? values.transaction_date.format("YYYY-MM-DD HH:mm:ss") : null,
        note: paymentDetails.note || null,
      });

      if (res?.success) {
        message.success("Bulk payment processed. Invoices updated FIFO (oldest first).");
        setIsRepayOpen(false);
        repayForm.resetFields();
        setBulkPayAmount(0);
        setPaymentMethodCode("");
        setPaymentDetails({
          amount: 0,
          note: "",
          bank_account_id: "",
          transaction_no: "",
          reference_no: "",
          cheques: [],
        });
        handleRefreshAll();
      } else {
        message.error(res?.message || "Failed to process bulk payment.");
      }
    } catch (err) {
      console.error(err);
      message.error("An error occurred.");
    }
  };

  const totalOutstandingDue = outstandingPurchases?.data
    ? outstandingPurchases.data.reduce((sum: number, p: any) => sum + Number(p.due_amount), 0)
    : Array.isArray(outstandingPurchases)
    ? outstandingPurchases.reduce((sum: number, p: any) => sum + Number(p.due_amount), 0)
    : 0;

  // FIFO Allocation preview calculations
  const fifoAllocations = (() => {
    let remaining = bulkPayAmount;
    const purchasesList = outstandingPurchases?.data || outstandingPurchases || [];
    return purchasesList.map((p: any) => {
      if (remaining <= 0) return { ...p, allocated: 0, newDue: p.due_amount };
      const allocated = Math.min(remaining, p.due_amount);
      remaining -= allocated;
      return { ...p, allocated, newDue: p.due_amount - allocated };
    });
  })();

  // Columns Definitions
  const creditColumns = [
    {
      title: "Date",
      dataIndex: "transaction_date",
      key: "transaction_date",
      render: (v: string) => (
        <span className="text-xs text-gray-600 flex items-center gap-1.5 font-medium">
          <Calendar className="w-3.5 h-3.5 text-gray-400" />
          {dayjs(v).format("YYYY-MM-DD HH:mm")}
        </span>
      ),
    },
    {
      title: "Purchase Invoice No",
      dataIndex: "invoice_no",
      key: "invoice_no",
      render: (v: string) =>
        v ? (
          <span className="text-xs font-bold text-indigo-600">Inv: {v}</span>
        ) : (
          <span className="text-gray-300 text-xs">-</span>
        ),
    },
    {
      title: "Transaction Type",
      dataIndex: "transaction_type_name",
      key: "transaction_type_name",
      render: (v: string, record: any) => {
        const code = record.transaction_type_code?.toLowerCase();
        const isIssued = code === "credit_issued" || code === "issued";
        return (
          <Tag
            color={isIssued ? "rose" : "blue"}
            className="flex items-center gap-1 w-fit text-[10px] font-bold uppercase"
          >
            {isIssued ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {v}
          </Tag>
        );
      },
    },
    {
      title: "Remarks",
      dataIndex: "note",
      key: "note",
      width: 250,
      render: (v: string) => (
        <span className="text-xs text-gray-500 block max-w-xs truncate" title={v}>
          {v || <span className="text-gray-300">No remarks</span>}
        </span>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      align: "right" as const,
      render: (v: number) => (
        <span className="font-bold text-gray-900 text-xs">
          LKR {v.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      align: "center" as const,
      render: (_: any, record: any) => {
        const menuItems = [
          {
            key: "view",
            label: "View Details",
            icon: <EyeOutlined />,
            onClick: () => {
              setSelectedRecord(record);
              setIsDetailOpen(true);
            },
          },
        ];
        return (
          <Dropdown menu={{ items: menuItems }} trigger={["click"]} placement="bottomRight">
            <Button type="text" size="small" icon={<MoreOutlined />} className="text-gray-400" />
          </Dropdown>
        );
      },
    },
  ];

  const purchaseColumns = [
    {
      title: "Invoice No",
      dataIndex: "invoice_no",
      key: "invoice_no",
      render: (v: string) => <span className="font-bold text-xs text-indigo-600 font-mono">Inv: {v}</span>,
    },
    {
      title: "PO Ref",
      dataIndex: "po_reference",
      key: "po_reference",
      render: (v: string) => <span className="text-xs text-gray-400 font-mono font-medium">{v || "—"}</span>,
    },
    {
      title: "Date",
      dataIndex: "purchase_date",
      key: "purchase_date",
      render: (v: string) => (
        <span className="text-xs text-gray-500 font-medium">
          {dayjs(v).format("YYYY-MM-DD")}
        </span>
      ),
    },
    {
      title: "Grand Total",
      dataIndex: "grand_total",
      key: "grand_total",
      align: "right" as const,
      render: (v: number) => <span className="text-xs font-semibold">Rs. {Number(v).toFixed(2)}</span>,
    },
    {
      title: "Paid",
      dataIndex: "paid_amount",
      key: "paid_amount",
      align: "right" as const,
      render: (v: number) => <span className="text-xs text-emerald-600 font-semibold">Rs. {Number(v).toFixed(2)}</span>,
    },
    {
      title: "Due",
      dataIndex: "due_amount",
      key: "due_amount",
      align: "right" as const,
      render: (v: number) => (
        <span className={`text-xs font-bold ${v > 0 ? "text-rose-600" : "text-gray-400"}`}>
          Rs. {Number(v).toFixed(2)}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v: string) => {
        let color = "blue";
        if (v === "Draft") color = "gray";
        if (v === "Pending") color = "orange";
        if (v === "Completed") color = "green";
        if (v === "Returned") color = "red";
        return <Tag color={color} className="text-[10px] font-bold uppercase">{v}</Tag>;
      },
    },
  ];

  const paymentColumns = [
    {
      title: "Payment No",
      dataIndex: "payment_no",
      key: "payment_no",
      render: (v: string) => <span className="font-bold text-xs text-indigo-600 font-mono">{v}</span>,
    },
    {
      title: "Date",
      dataIndex: "payment_date",
      key: "payment_date",
      render: (v: string) => (
        <span className="text-xs text-gray-500 font-medium">{dayjs(v).format("YYYY-MM-DD HH:mm")}</span>
      ),
    },
    {
      title: "Invoice Ref",
      dataIndex: "invoice_no",
      key: "invoice_no",
      render: (v: string) => <span className="text-xs font-semibold text-gray-700">Inv: {v}</span>,
    },
    {
      title: "Method",
      dataIndex: "payment_method_name",
      key: "payment_method_name",
      render: (v: string) => <Tag color="blue" className="text-[10px] font-bold uppercase">{v}</Tag>,
    },
    {
      title: "Amount Paid",
      dataIndex: "amount",
      key: "amount",
      align: "right" as const,
      render: (v: number) => <span className="text-xs font-bold text-emerald-600">Rs. {Number(v).toFixed(2)}</span>,
    },
    {
      title: "Note / Details",
      dataIndex: "note",
      key: "note",
      render: (v: string, rec: any) => (
        <div className="flex flex-col gap-1.5 py-1">
          {v && <span className="text-xs text-gray-700 font-medium">{v}</span>}
          <PaymentMethodDetails
            type={rec.payment_method_code || ""}
            data={{
              transaction_no: rec.card_transaction_no || rec.bank_reference_no,
              cheque_no: rec.cheque_no,
              bank_name: rec.cheque_bank_name,
              cheque_date: rec.cheque_date,
              deposit_bank_name: rec.bank_account_name,
            }}
          />
          {!v && !rec.cheque_no && !rec.card_transaction_no && !rec.bank_reference_no && (
            <span className="text-xs text-gray-300">—</span>
          )}
        </div>
      ),
    },
  ];

  const outstandingColumns = [
    {
      title: "Invoice Details",
      key: "invoice_no",
      render: (_: any, rec: any) => (
        <div>
          <span className="font-bold text-xs text-indigo-600 block">Inv: {rec.invoice_no}</span>
          <span className="text-[10px] text-gray-400 font-mono">PO: {rec.po_reference || "N/A"}</span>
        </div>
      ),
    },
    {
      title: "Purchase Date",
      dataIndex: "purchase_date",
      key: "purchase_date",
      render: (v: string) => <span className="text-xs text-gray-500 font-medium">{dayjs(v).format("YYYY-MM-DD")}</span>,
    },
    {
      title: "Grand Total",
      dataIndex: "grand_total",
      key: "grand_total",
      align: "right" as const,
      render: (v: number) => <span className="text-xs text-gray-600">Rs. {Number(v).toFixed(2)}</span>,
    },
    {
      title: "Remaining Due",
      dataIndex: "due_amount",
      key: "due_amount",
      align: "right" as const,
      render: (v: number) => <span className="text-xs font-bold text-rose-600">Rs. {Number(v).toFixed(2)}</span>,
    },
  ];

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      {/* Search Header */}
      <PageHeader
        title="Supplier Accounts Payable"
        subText="Manage supplier balance ledgers, purchase invoice history, and credit repayment allocation"
        handleButtons={[
          { title: "Refresh", icon: RotateCcw as any, onClick: handleRefreshAll },
        ]}
        searchBar={
          <div className="flex items-center gap-3">
            {urlId ? (
              <Tag color="purple" className="flex items-center gap-1 text-xs py-1 px-2.5 font-semibold font-sans">
                <Lock className="w-3.5 h-3.5 text-purple-600" /> Redirect Locked Mode
              </Tag>
            ) : (
              <Select
                allowClear
                placeholder="Search Supplier Profile..."
                onChange={(v) => {
                  setSupplierId(v);
                  setCreditPage(1);
                  setPurchasePage(1);
                  setReturnPage(1);
                  setPaymentPage(1);
                }}
                className="w-72 h-9 text-xs"
                showSearch
                optionFilterProp="children"
              >
                {suppliers.map((s: any) => (
                  <Select.Option key={s.id} value={s.id}>
                    {s.name} {s.phone ? `(${s.phone})` : ""}
                  </Select.Option>
                ))}
              </Select>
            )}
          </div>
        }
      />

      {!supplierId ? (
        <div className="mt-8 bg-white border border-gray-100 rounded-2xl p-12 text-center shadow-sm max-w-lg mx-auto">
          <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-gray-800 font-extrabold text-base mb-1">Select Supplier Account</h3>
          <p className="text-gray-400 text-xs leading-relaxed">
            Please search and select a supplier from the dropdown above to load their deep account statement, purchases invoice history, returns, payments history, and outstanding accounts payable list.
          </p>
        </div>
      ) : (
        <div className="mt-6 flex flex-col gap-6">
          {/* Supplier Profile Summary Bar */}
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm flex flex-col lg:flex-row lg:items-center justify-between gap-6 transition-all duration-300 hover:shadow-md">
            <div className="flex flex-col gap-1.5">
              <span className="text-[10px] text-indigo-600 font-extrabold uppercase tracking-wider">
                Supplier Profile
              </span>
              <h2 className="text-lg font-black text-gray-800 flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-gray-400" />
                {selectedSupplier?.name || "Loading name..."}
              </h2>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                {selectedSupplier?.company_name && <span>Company: <b>{selectedSupplier.company_name}</b></span>}
                {selectedSupplier?.phone && <span>Phone: <b>{selectedSupplier.phone}</b></span>}
                {selectedSupplier?.email && <span>Email: <b>{selectedSupplier.email}</b></span>}
              </div>
            </div>

            {supplierBalance && (
              <div className="flex flex-wrap items-center gap-6">
                <div className="flex flex-col">
                  <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                    Outstanding Payable
                  </span>
                  <span className={`text-xl font-black ${supplierBalance.balance > 0 ? "text-rose-600" : "text-emerald-600"}`}>
                    LKR {supplierBalance.balance?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                </div>

                <div className="h-10 w-[1px] bg-gray-100 hidden sm:block" />

                <div className="flex flex-col">
                  <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                    Total Purchase Credit
                  </span>
                  <span className="text-sm font-extrabold text-gray-800 mt-0.5">
                    LKR {supplierBalance.total_issued?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                </div>

                <div className="h-10 w-[1px] bg-gray-100 hidden sm:block" />

                <div className="flex flex-col">
                  <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                    Total Payments Settled
                  </span>
                  <span className="text-sm font-extrabold text-gray-700 mt-0.5">
                    LKR {supplierBalance.total_paid?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            )}

            <div>
              <Button
                type="primary"
                className="bg-indigo-600 hover:bg-indigo-700 border-none flex items-center gap-2 h-10 px-5 text-xs font-bold rounded-lg shadow-sm hover:shadow transition-all duration-200"
                onClick={() => {
                  repayForm.resetFields();
                  setBulkPayAmount(0);
                  setPaymentMethodCode("");
                  setIsRepayOpen(true);
                }}
              >
                Record Bulk Payment
              </Button>
            </div>
          </div>

          {/* Details Tabs Section */}
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden p-6">
            <Tabs defaultActiveKey="overview" onChange={setActiveTab} className="ledger-tabs">
              {/* Tab 1: Credit Ledger */}
              <Tabs.TabPane
                tab={
                  <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider">
                    <FileText className="w-4 h-4" /> Credit Ledger Statement
                  </span>
                }
                key="overview"
              >
                <div className="pt-2">
                  <Table
                    dataSource={creditLogs?.data || creditLogs || []}
                    columns={creditColumns}
                    rowKey="id"
                    loading={isCreditLoading}
                    pagination={tablePagination({
                      current: creditPage,
                      pageSize: PAGE_SIZE,
                      total: creditLogs?.total || 0,
                      onChange: setCreditPage,
                      itemName: "supplier logs",
                    })}
                    scroll={{ x: 800 }}
                  />
                </div>
              </Tabs.TabPane>

              {/* Tab 2: Purchase History */}
              <Tabs.TabPane
                tab={
                  <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider">
                    <DollarSign className="w-4 h-4" /> Purchases History
                  </span>
                }
                key="purchases"
              >
                <div className="pt-2">
                  <Table
                    dataSource={purchasesData?.data || purchasesData || []}
                    columns={purchaseColumns}
                    rowKey="id"
                    loading={isPurchasesLoading}
                    pagination={tablePagination({
                      current: purchasePage,
                      pageSize: PAGE_SIZE,
                      total: purchasesData?.total || 0,
                      onChange: setPurchasePage,
                      itemName: "purchases",
                    })}
                    scroll={{ x: 800 }}
                  />
                </div>
              </Tabs.TabPane>

              {/* Tab 3: Purchase Returns History */}
              <Tabs.TabPane
                tab={
                  <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider">
                    <Undo2 className="w-4 h-4" /> Purchase Returns
                  </span>
                }
                key="returns"
              >
                <div className="pt-2">
                  <Table
                    dataSource={returnsData?.data || returnsData || []}
                    columns={purchaseColumns} // uses same structure as purchases
                    rowKey="id"
                    loading={isReturnsLoading}
                    pagination={tablePagination({
                      current: returnPage,
                      pageSize: PAGE_SIZE,
                      total: returnsData?.total || 0,
                      onChange: setReturnPage,
                      itemName: "returned purchases",
                    })}
                    scroll={{ x: 800 }}
                  />
                </div>
              </Tabs.TabPane>

              {/* Tab 4: Payments History */}
              <Tabs.TabPane
                tab={
                  <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider">
                    <FileText className="w-4 h-4" /> Logged Payments History
                  </span>
                }
                key="payments"
              >
                <div className="pt-2">
                  <Table
                    dataSource={paymentsData?.data || paymentsData || []}
                    columns={paymentColumns}
                    rowKey="id"
                    loading={isPaymentsLoading}
                    pagination={tablePagination({
                      current: paymentPage,
                      pageSize: PAGE_SIZE,
                      total: paymentsData?.total || 0,
                      onChange: setPaymentPage,
                      itemName: "purchase payments",
                    })}
                    scroll={{ x: 900 }}
                  />
                </div>
              </Tabs.TabPane>

              {/* Tab 5: Outstanding Invoices */}
              <Tabs.TabPane
                tab={
                  <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider">
                    <TrendingUp className="w-4 h-4 text-rose-500" /> Outstanding Accounts Payable
                  </span>
                }
                key="outstanding"
              >
                <div className="pt-2">
                  <Table
                    dataSource={outstandingPurchases?.data || outstandingPurchases || []}
                    columns={outstandingColumns}
                    rowKey="id"
                    loading={isOutstandingLoading}
                    pagination={false}
                    locale={{
                      emptyText: (
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description="This supplier has no outstanding accounts payable."
                        />
                      ),
                    }}
                  />
                </div>
              </Tabs.TabPane>
            </Tabs>
          </div>
        </div>
      )}

      {/* Detail Drawer */}
      <Drawer
        title={drawerTitle("Supplier Credit Log Details")}
        open={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        footer={drawerFooter({
          actions: [{ label: "Close", onClick: () => setIsDetailOpen(false) }],
        })}
        width={500}
      >
        {selectedRecord && (
          <Descriptions column={1} bordered className="mt-4">
            <Descriptions.Item label="Date">
              {dayjs(selectedRecord.transaction_date).format("YYYY-MM-DD HH:mm")}
            </Descriptions.Item>
            <Descriptions.Item label="Transaction Type">
              {selectedRecord.transaction_type_name}
            </Descriptions.Item>
            <Descriptions.Item label="Purchase Invoice">
              {selectedRecord.invoice_no || <span className="text-gray-300">-</span>}
            </Descriptions.Item>
            <Descriptions.Item label="Amount">
              LKR {selectedRecord.amount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </Descriptions.Item>
            <Descriptions.Item label="Remarks / Note">
              {selectedRecord.note || <span className="text-gray-300">No remarks</span>}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>

      {/* Record Bulk Payment Modal */}
      <Modal
        title={modalTitle("Record Bulk Supplier Repayment")}
        open={isRepayOpen}
        onCancel={() => {
          setIsRepayOpen(false);
          repayForm.resetFields();
          setBulkPayAmount(0);
          setPaymentMethodCode("");
        }}
        onOk={() => repayForm.submit()}
        confirmLoading={bulkPayMutation.isPending}
        okText="Record Payment"
        okButtonProps={{
          size: "middle",
          disabled: bulkPayAmount <= 0 || bulkPayAmount > totalOutstandingDue + 0.001,
        }}
        cancelButtonProps={{ size: "middle" }}
        width={650}
      >
        <Form
          form={repayForm}
          layout="vertical"
          onFinish={handleSaveBulkPay}
          className="mt-4"
          initialValues={{ transaction_date: dayjs() }}
        >
          <Form.Item
            name="payment_method_id"
            label="Payment Method"
            rules={[{ required: true, message: "Please select payment method" }]}
          >
            <Select
              placeholder="Select payment method"
              className="h-9"
              onChange={(val) => {
                const pm = paymentMethods.find((m: any) => m.id === val);
                setPaymentMethodCode(pm ? pm.code : "");
                setPaymentDetails({
                  amount: 0,
                  note: "",
                  bank_account_id: "",
                  transaction_no: "",
                  reference_no: "",
                  cheques: [],
                });
                setBulkPayAmount(0);
              }}
            >
              {paymentMethods.map((pm: any) => (
                <Select.Option key={pm.id} value={pm.id}>
                  {pm.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {paymentMethodCode && (
            <div className="mb-4 pt-2 border-t border-gray-100">
              <PaymentOption
                paymentMethodCode={paymentMethodCode}
                value={paymentDetails}
                onChange={(val) => {
                  setPaymentDetails(val);
                  setBulkPayAmount(val.amount || 0);
                }}
                bankAccounts={bankAccounts}
              />
            </div>
          )}

          <Form.Item
            name="transaction_date"
            label="Payment Date"
            rules={[{ required: true, message: "Please select date" }]}
          >
            <DatePicker className="w-full h-9" showTime format="YYYY-MM-DD HH:mm" />
          </Form.Item>

          {/* FIFO Allocation Preview */}
          {bulkPayAmount > 0 && totalOutstandingDue > 0 && (
            <div className="mt-4 border border-gray-100 rounded-lg p-4 bg-gray-50/50">
              <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <CheckCircleOutlined className="text-indigo-500" /> FIFO Repayment Preview
              </h4>
              <div className="max-h-48 overflow-y-auto border border-gray-100 rounded bg-white">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100 font-semibold text-gray-500">
                      <th className="p-2">Invoice</th>
                      <th className="p-2 text-right">Outstanding Due</th>
                      <th className="p-2 text-right text-indigo-600">Allocated</th>
                      <th className="p-2 text-right">New Due</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fifoAllocations.map((item: any) => (
                      <tr key={item.id} className="border-b border-gray-50 last:border-0">
                        <td className="p-2 font-medium">
                          Inv: {item.invoice_no}
                          {item.allocated >= item.due_amount && (
                            <span className="ml-1.5 inline-flex items-center px-1 py-0.5 rounded text-[9px] font-bold bg-emerald-50 text-emerald-600">
                              ✓ Settled
                            </span>
                          )}
                        </td>
                        <td className="p-2 text-right">Rs. {Number(item.due_amount).toFixed(2)}</td>
                        <td className="p-2 text-right font-bold text-indigo-600">
                          {item.allocated > 0 ? `Rs. ${item.allocated.toFixed(2)}` : "—"}
                        </td>
                        <td className="p-2 text-right font-medium text-gray-700">
                          Rs. {item.newDue.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Form>
      </Modal>
    </div>
  );
};
